<template>
  <main id="mainContent" class="modification">
    <Stepper v-if="isFromNewRes" :step="3" />
    <CancellationStatus :show-message="hasNotificationError" />
    <ModifyAlert :appointment="disallowedAppointment"/>
    <div class="app-main-content">
      <Timer v-if="isFromNewRes" ref="timer"/>
      <template v-if="isDeleted">
        <h1>{{ screenDataAlt.title }}</h1>
        <p>{{ screenDataAlt.description }}</p>
        <p  v-if="checkConfirmationNum">Please log in again to see your updated reservation details on your customer portal profile page</p>
        <RiDmvHome />
        <div style="height: 2rem;"></div>
      </template>
      <template v-else>
        <h1 v-if="isFromNewRes">You already have a reservation scheduled</h1>
        <h1 v-else>{{ screenData.title }}</h1>
        <p v-if="!isFromNewRes">{{ screenData.description }}</p>
        <ReservationList :list-found="reservationsFound" :is-from-new-res="isFromNewRes" 
                         @doDeleteSuccess="deleted" @doModifyNotAllowed="modifyNotAllowed" 
                         @doModifyExisting="modifyExisting"/>
      </template>

      <BaseButtonGroup v-if="!checkConfirmationNum">
        <button type="button" @click="handleRouting(true)" v-if="!isFromNewRes">{{ language.btnResHome }}</button>
        <button type="button" class="app-btn-primary" v-if="isDeleted"
                @click="handleRouting(false)">{{ language.btnMakeRes }}</button>
        <button type="button" v-if="isFromNewRes && !isDeleted"
          @click="goToPreviousStep">Previous</button>
        <button type="button" class="app-btn-primary" v-if="isFromNewRes && !isDeleted"
          @click="continueNewReservation">Create new reservation</button>
      </BaseButtonGroup>
      <BaseButtonGroup v-if="checkConfirmationNum">
        <button @click="closeResPage()">{{ language.btnClose }}</button>
      </BaseButtonGroup>
    </div>
  </main>
</template>


<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import {  useRouter, useRoute } from 'vue-router';
import appConfig from '@/app.config';
import ReservationList from "@/components/reservation/modification/ReservationList";
import RiDmvHome from '@/components/RiDmvHome';
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';
import CancellationStatus from '@/components/reservation/modification/CancellationStatus';
import ModifyAlert from '@/components/reservation/modification/ModifyAlert';
import { useHead } from '@unhead/vue';
import Timer from '@/components/reservation/Timer.vue';
import Stepper from '@/components/reservation/Stepper.vue';

// usehead to manage the page meta 
useHead({
  title: `Reservation change | ${appConfig.shortName}`,
  meta: [
    {
      name: 'description',
      content: 'Modify or cancel an existing reservation. You can change your location or date and time.'
    }
  ]
});

// State and Actions
const store = useStore();
const router = useRouter();
const route = useRoute();

// Reactive state
const stepNum = 8;
const stepNumAlt = 9;
const isDeleted = ref(false);
const hasNotificationError = ref(false);
const disallowedAppointment = ref({});

const language = computed(() => store.state.user.LANG);
const screenData = computed(() => store.state.user.STEPS[stepNum]);
const screenDataAlt = computed(() => store.state.user.STEPS[stepNumAlt]);
const checkConfirmationNum = computed(() => localStorage.getItem('confirmationNumber') != undefined && localStorage.getItem('confirmationNumber') != null);
const reservationsFound = computed(() => store.state.user.reservationsFound);
const PAGES = computed(() => store.state.user.PAGES);

// True when this page was reached during the new-reservation flow
const isFromNewRes = computed(() => route.query.from === 'newres');

// Lifecycle Hooks
onMounted(async () => {

});

onUnmounted(() => {
  localStorage.removeItem('onlineToken');
  localStorage.removeItem('confirmationNumber');
});

// Methods
const applySnapshot = () => {
  const saveNewState = store.state.user.newResSnapshot;
  if (!saveNewState) return;
  store.dispatch('user/setStoreProperty', { property: 'resType', value: saveNewState.resType });
  store.commit('user/setState', { property: 'resDetails', value: JSON.parse(JSON.stringify(saveNewState.resDetails)) });
  store.dispatch('user/setStoreProperty', { property: 'resFeeApplicable', value: saveNewState.resFeeApplicable });
  store.dispatch('user/setStoreProperty', { property: 'resTypeForCustomer', value: saveNewState.resTypeForCustomer });
}
/**
 * Restores the new-reservation store state (resDetails, resFeeApplicable, resTypeForCustomer)
 * which may have been overwritten by setReservationForUpdate when the user interacted with
 * the existing reservation list.
 */
const restoreNewResState = () => {
  applySnapshot();
  store.dispatch('user/setStoreProperty', { property: 'resSelectedForUpdate', value: {} });
  store.dispatch('user/setStoreProperty', { property: 'modifyReservation', value: false });
};

const deleted = async (hasError, remainingCount = 0) => {
  if (isFromNewRes.value) {
    // Cancelling (even just opening the confirm modal) corrupted resDetails/resSelectedForUpdate
    // via setReservationForUpdate — restore the original new-attempt data regardless of outcome
    restoreNewResState();
    
    if (remainingCount > 0) {
      // More reservations remain, but the one selected for deletion was cancelled. stay on this page to let user act on the remaining reservations.
      hasNotificationError.value = hasError;
      return;
    }
    router.push({ name: PAGES.value.confirmRes });
    return;
  }
  // no more business with previously selected reservation... reset
  store.dispatch('user/resetReservationDetails', true);
  isDeleted.value = true;
  hasNotificationError.value = hasError;
};
/**
 * Only used by cancelled (delete okay) feedback, not by reservation listing
 * @param isHome
 */
const handleRouting = (isHome) => {
  if (isHome) {
    router.push({ name: PAGES.value.home });
  } else {
    router.push({ name: PAGES.value.selectType });
  }
};

const modifyNotAllowed = (data) => {
  disallowedAppointment.value = data;
};

const closeResPage = () => {
  localStorage.removeItem('onlineToken');
  localStorage.removeItem('confirmationNumber');
  window.close();
};

const modifyExisting = async (res,) => {
  // Re-apply the NEW attempt's type/date/time/location — Cancel on another reservation
  applySnapshot();
  store.dispatch('user/setStoreProperty', { property: 'resSelectedForUpdate', value: res });
  const details = store.state.user.resDetails;
  if(details.userInfo?.reservationSendConfirmationRequest) {
    store.commit('user/setState', { 
      property: 'resDetails', 
      value: {
        ...details,
        userInfo: {
          ...details.userInfo,
          reservationSendConfirmationRequest: {
            ...details.userInfo.reservationSendConfirmationRequest,
            isModifyRequest: true
          }
        }
      } 
    });
  }
  store.dispatch('user/setStoreProperty', { property: 'modifyReservation', value: true });
  router.push({ name: PAGES.value.confirmRes, query: { from: 'modifyExisting' } });
};

// Continues with the new reservation without touching the existing one
const continueNewReservation = async () => {
  await restoreNewResState();
  router.push({ name: PAGES.value.confirmRes, query: { from: 'newres' } });
};

const goToPreviousStep = () => {
  // covers the case where Cancel's confirm modal was opened (corrupting state) then aborted without deleting
  restoreNewResState();
  router.push({ name: PAGES.value.userInfo });
};
</script>

<style lang="scss" scoped>
@import "~animate.css";
.app-button-group {
  button {
    margin-bottom: 1.125em;
    &:not(:last-child) {
      margin-right: 1em;
    }
    &.disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
}
</style>